<template>
    <article v-sync-bodylock-gap
             class="c-brandheader"
             :style="topHeaderStyle">
        <div class="o-chrome-container h-full">
            <div class="flex justify-between items-center h-full">
                <BrandLogo v-for="(brandLogo, ix) in brandLogos"
                           :key="ix"
                           :content="brandLogo"/>
            </div>
        </div>
    </article>
</template>

<script setup lang="ts">
import { watch, ref, computed, StyleValue, onMounted, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { BrandLogoViewModel, RequestBreakpoint } from '@/types/apiServerContract';
import breakpointsState from '@/core/responsive/breakpoints/breakpointsState.observable';
import { XS, SM, MD } from '@/core/config/breakpointConfig';
import BrandLogo from './BrandLogo.vue';
import Api from '@/project/http/Api.service';
import adnuntiusService from '@/project/personalization/adnuntius.service';
import themeService from '@/project/app/themes/theme.service';
import { hexToRgbValues } from '@/core/color/color.service';
import loggingService from '@/core/logging.service';
import { HttpCancelError } from '@/core/http/HttpCancelError';

const route = useRoute();

const props = defineProps({
    content: {
        type: Object as () => BrandLogoViewModel | null,
        required: false,
        default: null
    }
});

const brandLogos = ref<BrandLogoViewModel[]>([]);

async function getBrandLogos(breakpoint: RequestBreakpoint) {
    await nextTick();

    try {
        brandLogos.value = await Api.personalization.brandLogos(
            breakpoint,
            route.fullPath,
            adnuntiusService.getContactKey(),
            adnuntiusService.getCustomerKey());
    } catch (e) {
        if (e instanceof HttpCancelError) return;
        loggingService.error(e);
    }
}

const activeBreakpoint = computed((): RequestBreakpoint => {
    switch (breakpointsState.activeBreakpoint) {
        case XS:
        case SM:
            return RequestBreakpoint.Mobile;
        case MD:
            return RequestBreakpoint.Tablet;
        default:
            return RequestBreakpoint.Desktop;
    }
});

const topHeaderStyle = computed((): StyleValue => {
    const rgb = themeService.theme?.topColor && hexToRgbValues(themeService.theme.topColor);

    return {
        ...(rgb && { backgroundColor: `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, ${themeService.takeOverOpacity})` }),
        ...(themeService.theme?.topColor && { borderColor: themeService.theme.topColor })
    };
});

watch(() => activeBreakpoint.value, (value: RequestBreakpoint) => {
    getBrandLogos(value);
});

watch(() => route.fullPath, (newPath: string, oldPath?: string) => {
    if (newPath !== oldPath) {
        getBrandLogos(activeBreakpoint.value);
    }
});

onMounted(() => {
    nextTick(() => {
        getBrandLogos(activeBreakpoint.value);
    });
});
</script>

<style lang="less">
    .c-brandheader {
        border-bottom: 1px solid;
        @apply border-gray-500 h-full bg-white text-black;

        transition: background-color 500ms ease-in-out, color 500ms ease-in-out, border-color 500ms ease-in-out;
    }
</style>
